import { Dispatch } from 'redux';
import { StoreState } from '../store-interfaces';
import CrudActionContainer from './CrudActionContainer';
import { getActionHookBuilder } from './action-hook-builder';
import commonHandler from '../errorHandlers/common-handler';
import GoogleAdsController from '../controllers/GoogleAdsController';
import AdAudienceController from '../controllers/AdAudienceController';
import adAudiencesHandler from '../errorHandlers/ad-audience-handler';
import {
  AdAudienceCreateUpdatePayload,
  GoogleAdAudienceCreateUpdatePayload,
} from '../model/AdAudienceModel';
import { INVITED_USER_AUTH_ERROR } from './ConnectedPageCRUDActions';
import { getErrorJson } from 'packages/utils/index';

class GoogleAdsCRUDActions extends CrudActionContainer {
  resetAction: string;
  retrieveGoogleClientCredentialsAction: string;
  retrieveGoogleClientCredentialsSuccessAction: string;
  retrieveGoogleClientCredentialsErrorAction: string;
  createGoogleClientCredentialsAction: string;
  createGoogleClientCredentialsSuccessAction: string;
  createGoogleClientCredentialsErrorAction: string;
  deleteGoogleClientCredentialsAction: string;
  deleteGoogleClientCredentialsSuccessAction: string;
  deleteGoogleClientCredentialsErrorAction: string;

  constructor() {
    super('GOOGLE_ADS');
    this.resetAction = super.makeActionKey('RESET');
    this.retrieveGoogleClientCredentialsAction = this.makeActionKey(
      'RETRIEVE_CLIENT_CREDENTIALS',
    );
    this.retrieveGoogleClientCredentialsSuccessAction = this.makeActionKey(
      'RETRIEVE_CLIENT_CREDENTIALS_SUCCESS',
    );
    this.retrieveGoogleClientCredentialsErrorAction = this.makeActionKey(
      'RETRIEVE_CLIENT_CREDENTIALS_ERROR',
    );
    this.createGoogleClientCredentialsAction = this.makeActionKey(
      'CREATE_CLIENT_CREDENTIALS',
    );
    this.createGoogleClientCredentialsSuccessAction = this.makeActionKey(
      'CREATE_CLIENT_CREDENTIALS_SUCCESS',
    );
    this.createGoogleClientCredentialsErrorAction = this.makeActionKey(
      'CREATE_CLIENT_CREDENTIALS_ERROR',
    );
    this.deleteGoogleClientCredentialsAction = this.makeActionKey(
      'DELETE_CLIENT_CREDENTIALS',
    );
    this.deleteGoogleClientCredentialsSuccessAction = this.makeActionKey(
      'DELETE_CLIENT_CREDENTIALS_SUCCESS',
    );
    this.deleteGoogleClientCredentialsErrorAction = this.makeActionKey(
      'DELETE_CLIENT_CREDENTIALS_ERROR',
    );
  }

  retrieveGoogleClientCredentials(
    ...params: Parameters<
      typeof GoogleAdsController.retrieveGoogleClientCredentials
    >
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveGoogleClientCredentialsAction));
        const payload =
          await GoogleAdsController.retrieveGoogleClientCredentials(...params);
        dispatch({
          type: this.retrieveGoogleClientCredentialsSuccessAction,
          payload,
        });
        return payload;
      } catch (e) {
        dispatch({
          type: this.retrieveGoogleClientCredentialsSuccessAction,
          payload: e,
        });
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  createGoogleClientCredentials(
    ...params: Parameters<
      typeof GoogleAdsController.createGoogleClientCredentials
    >
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createGoogleClientCredentialsAction));
        const payload = await GoogleAdsController.createGoogleClientCredentials(
          ...params,
        );
        dispatch({
          type: this.createGoogleClientCredentialsSuccessAction,
          payload,
        });
        return payload;
      } catch (e) {
        dispatch({
          type: this.createGoogleClientCredentialsErrorAction,
          payload: e,
        });
        commonHandler(dispatch)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  deleteGoogleClientCredentials(
    ...params: Parameters<
      typeof GoogleAdsController.deleteGoogleClientCredentials
    >
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteGoogleClientCredentialsAction));
        const payload = await GoogleAdsController.deleteGoogleClientCredentials(
          ...params,
        );
        dispatch({
          type: this.deleteGoogleClientCredentialsSuccessAction,
          payload,
        });
        return payload;
      } catch (e) {
        dispatch({
          type: this.deleteGoogleClientCredentialsErrorAction,
          payload: e,
        });
        commonHandler(dispatch)(e);
        throw e;
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  assignAdAudience(facebookPageId: number, audienceId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const res = await AdAudienceController.assignAdAudience(
          facebookPageId,
          audienceId,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      }
    };
  }

  createGoogleAdAudience(
    facebookPageId: number,
    adAudience: Partial<GoogleAdAudienceCreateUpdatePayload>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));

        const res = await AdAudienceController.createGoogleAdAudience(
          facebookPageId,
          adAudience,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  updateAdAudienceById(
    facebookPageId: number,
    adAudienceId: number | string,
    update: Partial<AdAudienceCreateUpdatePayload>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));

        const res = await AdAudienceController.updateAdAudienceByRemoteId(
          facebookPageId,
          adAudienceId,
          update,
        );

        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return adAudiencesHandler(dispatch, this)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieve(facebookPageId: number, audienceId: number, remoteId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAction));

        const res = await AdAudienceController.getAudience(
          facebookPageId,
          audienceId,
          remoteId,
        );
        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAudienceById(
    facebookPageId: number,
    adAudienceRemoteId: string | number,
    catchManually?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAction));

        const res = await AdAudienceController.retrieveAudienceByRemoteId(
          facebookPageId,
          adAudienceRemoteId,
        );
        dispatch(this.retrieved(res.adAudience));
        return res;
      } catch (e) {
        if (catchManually) {
          throw e;
        }

        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  syncAudienceById(facebookPageId: number, adAudienceId: string | number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(''));

        const res = await GoogleAdsController.syncAudienceById(
          facebookPageId,
          adAudienceId,
        );
        if (res.adAudience) {
          dispatch(this.retrieved(res.adAudience));
          return res;
        }
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  deleteAudienceById(facebookPageId: number, adAudienceId: string | number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.deleteAction));
        const res = await AdAudienceController.deleteAudienceByRemoteId(
          facebookPageId,
          adAudienceId,
        );
        dispatch(this.deleteLocal(res.adAudience));
        return res;
      } catch (e) {
        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  retrieveAll(facebookPageId: number, adAccountId?: number | string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const res = await AdAudienceController.retrieveAudiences(
          facebookPageId,
          adAccountId,
          'google',
        );
        dispatch(this.retrievedAll(res.adAudiences));
        return res;
      } catch (e) {
        try {
          const errorJson = await getErrorJson(e);

          if (errorJson.error_code === INVITED_USER_AUTH_ERROR) {
            return;
          }
        } catch (parseError) {
          // eslint-disable-next-line no-console
          console.error(e);
        }

        return commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  resetAdAudiences() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.resetAction,
      });
    };
  }
}

const googleAdsCRUDActions = new GoogleAdsCRUDActions();

export const useGoogleAdsCRUDActions = getActionHookBuilder(
  googleAdsCRUDActions,
  [
    'retrieveGoogleClientCredentials',
    'createGoogleClientCredentials',
    'deleteGoogleClientCredentials',
    'retrieveAll',
    'createGoogleAdAudience',
    'updateAdAudienceById',
    'assignAdAudience',
    'retrieveAudienceById',
    'syncAudienceById',
    'deleteAudienceById',
    'resetAdAudiences',
  ],
);

export default googleAdsCRUDActions;
